import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { createTenant, type Tenant } from '../api/tenants.js';
import { DEFAULT_APP_TIME_ZONE, TIME_ZONE_OPTIONS } from '@zengine/tz';
import { createUser } from '../api/users.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

type FormTier = Tenant['tier'] | 'enterprise';

const FormSchema = z.object({
  slug: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9-]+$/, 'Sólo minúsculas, números y guion.'),
  name: z.string().trim().min(1).max(120),
  tier: z.enum(['logical', 'physical']),
  defaultLocale: z.string().trim().min(2).max(10).optional(),
  appTimeZone: z.string().trim().min(1).optional(),
});

const LOCALES = ['es-CL', 'es', 'en', 'en-US', 'pt-BR'];

export function TenantNew(): JSX.Element {
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [tier, setTier] = useState<FormTier>('logical');
  const [locale, setLocale] = useState('es-CL');
  const [timeZone, setTimeZone] = useState(DEFAULT_APP_TIME_ZONE);
  const [createRoot, setCreateRoot] = useState(true);
  const [rootEmail, setRootEmail] = useState('');
  const [rootName, setRootName] = useState('');
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [successHint, setSuccessHint] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (body: {
      slug: string;
      name: string;
      tier: Tenant['tier'];
      defaultLocale?: string;
      appTimeZone?: string;
    }) => {
      const tenant = await createTenant(body);
      // Si el usuario eligió crear root admin, lo creamos después del tenant.
      // El backend ya agregó el callback Cognito (F4.1) y el invite envía email
      // automáticamente al primer login.
      if (createRoot && rootEmail.trim()) {
        try {
          await createUser(tenant.id, {
            email: rootEmail.trim(),
            ...(rootName.trim() ? { displayName: rootName.trim() } : {}),
            roles: ['admin'],
          });
        } catch (e) {
          // Si falla la creación del user, el tenant ya existe — lo dejamos pasar
          // con un mensaje para que el operador lo cree manualmente después.
          const msg = e instanceof ApiError ? e.message : (e as Error).message;
          setSuccessHint(
            `Tenant creado, pero falló crear el root admin: ${msg}. Creálo manualmente en /tenants/${tenant.id}/users.`,
          );
        }
      }
      return tenant;
    },
    onSuccess: (tenant) => {
      pushToast('success', t.tenant.created);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate(`/tenants/${tenant.id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 409) {
        pushToast('error', t.tenant.slugConflict);
        setErrMsg(t.tenant.slugConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
      setErrMsg(msg);
    },
  });

  function onSubmit(ev: React.FormEvent): void {
    ev.preventDefault();
    setErrMsg(null);
    if (tier === 'enterprise') {
      setErrMsg(t.tenant.enterpriseDisabled);
      return;
    }
    const parsed = FormSchema.safeParse({
      slug: slug.trim(),
      name,
      tier,
      defaultLocale: locale,
      appTimeZone: timeZone,
    });
    if (!parsed.success) {
      // BUG-P2 QA: mensajes Zod crudos como "String must contain at least 2
      // character(s)" sin nombre de campo. Mapeamos al campo + mensaje en español.
      const first = parsed.error.issues[0];
      const fieldLabels: Record<string, string> = { slug: 'slug', name: 'nombre', tier: 'tier', defaultLocale: 'idioma' };
      const field = String(first?.path?.[0] ?? '');
      const label = fieldLabels[field] ?? field;
      const rawMsg = first?.message ?? 'inválido';
      const friendly = rawMsg.includes('at least')
        ? `mínimo ${(rawMsg.match(/\d+/) ?? ['1'])[0]} caracteres`
        : rawMsg.includes('regex')
          ? 'formato inválido'
          : rawMsg;
      setErrMsg(label ? `Campo "${label}": ${friendly}` : friendly);
      return;
    }
    if (createRoot) {
      const e = rootEmail.trim();
      if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
        setErrMsg('Email del root admin inválido.');
        return;
      }
    }
    mutation.mutate(parsed.data);
  }

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">{t.tenant.newTitle}</h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.tenant.slug} helper={t.tenant.slugHelper}>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="acme-corp"
            className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-sm"
            required
          />
        </Field>

        <Field label={t.tenant.name}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ACME Corp"
            maxLength={120}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            required
          />
        </Field>

        <Field label={t.tenant.tier} helper={t.tenant.tierHelper}>
          <div className="space-y-2">
            <label className="flex items-start gap-2 rounded border border-slate-200 p-2">
              <input
                type="radio"
                name="tier"
                value="logical"
                checked={tier === 'logical'}
                onChange={() => setTier('logical')}
                className="mt-1"
              />
              <div>
                <div className="font-medium">{t.tenant.tierLabels.logical}</div>
                <div className="text-xs text-slate-500">{t.tenant.tierDesc.logical}</div>
              </div>
            </label>
            <label className="flex items-start gap-2 rounded border border-slate-200 p-2">
              <input
                type="radio"
                name="tier"
                value="physical"
                checked={tier === 'physical'}
                onChange={() => setTier('physical')}
                className="mt-1"
              />
              <div>
                <div className="font-medium">{t.tenant.tierLabels.physical}</div>
                <div className="text-xs text-slate-500">{t.tenant.tierDesc.physical}</div>
              </div>
            </label>
            <label
              className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 p-2 opacity-60"
              title={t.tenant.enterpriseSoon}
            >
              <input
                type="radio"
                name="tier"
                value="enterprise"
                checked={tier === 'enterprise'}
                onChange={() => setTier('enterprise')}
                disabled
                className="mt-1"
              />
              <div>
                <div className="font-medium">
                  {t.tenant.tierLabels.enterprise}{' '}
                  <span className="ml-1 rounded bg-amber-200 px-2 py-0.5 text-xs text-amber-900">
                    {t.tenant.enterpriseSoon}
                  </span>
                </div>
                <div className="text-xs text-slate-500">{t.tenant.tierDesc.enterprise}</div>
              </div>
            </label>
          </div>
        </Field>

        <Field label={t.tenant.locale}>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Zona horaria" helper="Para reportes/dashboards. Por defecto America/Bogota.">
          <select
            value={timeZone}
            onChange={(e) => setTimeZone(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            {TIME_ZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={createRoot}
              onChange={(e) => setCreateRoot(e.target.checked)}
            />
            <span className="font-medium">Crear usuario root admin del tenant</span>
          </label>
          <p className="text-xs text-slate-500">
            Se crea en Cognito + se envía email de invitación. El backend agrega automáticamente
            el callback URL del subdominio del tenant ({slug || 'slug'}.app.dev.zen.zervizdev.com).
          </p>
          {createRoot && (
            <>
              <Field label="Email del root admin">
                <input
                  type="email"
                  value={rootEmail}
                  onChange={(e) => setRootEmail(e.target.value)}
                  placeholder="admin@empresa.com"
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
              </Field>
              <Field label="Nombre (opcional)">
                <input
                  type="text"
                  value={rootName}
                  onChange={(e) => setRootName(e.target.value)}
                  placeholder="María Reyes"
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                />
              </Field>
            </>
          )}
        </div>

        {errMsg && (
          <p role="alert" className="text-xs text-red-700">
            {errMsg}
          </p>
        )}
        {successHint && (
          <p role="status" className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            {successHint}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to="/"
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {mutation.isPending ? '…' : t.tenant.create}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
    </label>
  );
}
